// The studio speaks English and Castilian Spanish. Text lives beside the code
// that shows it, one object per language: `es` must hold every entry `en` has,
// with the same shape, so a missing translation fails the typecheck. Entries
// that depend on a number or a name are functions.

export const LANGS = ["en", "es"] as const;
export type Lang = (typeof LANGS)[number];
export const LANG_NAMES: Record<Lang, string> = {
  en: "English",
  es: "Español",
};
/** Locale for dates and numbers (Spain writes 29 €, 1500, 6 oct). */
export const LOCALE: Record<Lang, string> = { en: "en-GB", es: "es-ES" };

export type Bilingual<T> = { en: T; es: T };
export const bilingual = <T>(text: { en: T; es: NoInfer<T> }): Bilingual<T> =>
  text;

export const isLang = (value: unknown): value is Lang =>
  LANGS.includes(value as Lang);
// Catalan, Basque and Galician readers in Spain read Castilian more surely than English.
const SPANISH = /^(es|ca|eu|gl)\b/;
/** The first of someone's preferred languages (most preferred first) that the studio speaks; English otherwise. */
export function preferredLang(tags: readonly string[]): Lang {
  for (const tag of tags) {
    const t = tag.trim().toLowerCase();
    if (t.startsWith("en")) return "en";
    if (SPANISH.test(t)) return "es";
  }
  return "en";
}
/** The language of a request: an explicit choice, else the preferred ones its Accept-Language header names. */
export function langOf(header?: string | null, choice?: unknown): Lang {
  if (isLang(choice)) return choice;
  return preferredLang(
    (header || "")
      .split(",")
      .map((part) => {
        const [tag, q] = part.split(";q=");
        return { tag, q: q === undefined ? 1 : Number(q) || 0 };
      })
      .sort((a, b) => b.q - a.q)
      .map((p) => p.tag),
  );
}
